import { Button, Checkbox } from '@carbon/react';
import { CenterCircle } from '@carbon/react/icons';
import { useCallback, useMemo } from 'react';
import { MineralType } from 'types/minerals';
import style from './fit-streams-selector.module.scss';
import {
  oilModelFormAtom,
  gasModelFormAtom,
  selectedFitStreamsAtom,
  waterModelFormAtom,
  selectedDcaScenarioAtom,
  selectedRecordVersionAtom,
  lockParamsAtom,
  fetchRegressionAtom,
  selectedTypeCurveAtom,
  earlyTimeStreamsAtom,
} from '../atoms';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Wellbore } from 'types/api-responses';

const ALL_STREAMS: MineralType[] = ['oil', 'gas', 'water'];

/**
 * Runs the DCA regression over the streams currently checked in Fit Streams.
 * Shared by the Fit button and the Shift+F hotkey so both send the same payload.
 *
 * @param {object} params - The well and its producing wellbore.
 * @return {Function} Callback that starts the regression.
 */
export const useFitCheckedStreams = ({
  uwi,
  producingWellBore,
}: {
  uwi: string;
  producingWellBore: Wellbore | null;
}) => {
  const selectedFitStreams = useAtomValue(selectedFitStreamsAtom);
  const selectedRecordVersion = useAtomValue(selectedRecordVersionAtom);
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const lockedParams = useAtomValue(lockParamsAtom);
  const selectedTypeCurve = useAtomValue(selectedTypeCurveAtom);
  const runRegression = useSetAtom(fetchRegressionAtom);

  return useCallback(() => {
    if (!selectedRecordVersion || !selectedDcaScenario) return;
    if (selectedFitStreams.length === 0) return;

    runRegression({
      wells: [
        {
          uwi: uwi,
          versionId: selectedRecordVersion.versionId,
          dcaScenarioId: selectedDcaScenario.id,
          oilModelFormAtom: oilModelFormAtom,
          gasModelFormAtom: gasModelFormAtom,
          waterModelFormAtom: waterModelFormAtom,
          regressStreams: selectedFitStreams,
          lockedParams: lockedParams,
          typeCurveId: selectedTypeCurve?.uniqueTcId ?? null,
          uwi12: producingWellBore?.uwi12 ?? '',
          perforatedLateralLengthFt:
            producingWellBore?.perforatedLateralLengthFt ?? null,
        },
      ],
    });
  }, [
    uwi,
    producingWellBore,
    selectedFitStreams,
    selectedRecordVersion,
    selectedDcaScenario,
    lockedParams,
    selectedTypeCurve?.uniqueTcId,
    runRegression,
  ]);
};

interface FitStreamsSelectorProps {
  disabled?: boolean;
  uwi: string;
  earlyTimeStreams: MineralType[];
  producingWellBore: Wellbore | null;
}

export const FitStreamsSelector = ({
  disabled = false,
  earlyTimeStreams,
  uwi,
  producingWellBore,
}: FitStreamsSelectorProps) => {
  const [selectedFitStreams, setSelectedFitStreams] = useAtom(
    selectedFitStreamsAtom
  );
  const selectedTypeCurve = useAtomValue(selectedTypeCurveAtom);
  const earlyTimeStreamsResponse = useAtomValue(earlyTimeStreamsAtom);

  const oilModelForm = useAtomValue(oilModelFormAtom);
  const gasModelForm = useAtomValue(gasModelFormAtom);
  const waterModelForm = useAtomValue(waterModelFormAtom);
  const oilDcaModelValue = useAtomValue(
    oilModelForm.declineModelAtomField
  ).value;
  const gasDcaModelValue = useAtomValue(
    gasModelForm.declineModelAtomField
  ).value;
  const waterDcaModelValue = useAtomValue(
    waterModelForm.declineModelAtomField
  ).value;
  const isAllSelected = useMemo(() => {
    return ALL_STREAMS.every((stream) => selectedFitStreams.includes(stream));
  }, [selectedFitStreams]);

  const handleAllChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedFitStreams(ALL_STREAMS);
      } else {
        setSelectedFitStreams([]);
      }
    },
    [setSelectedFitStreams]
  );

  const handleStreamChange = useCallback(
    (stream: MineralType, checked: boolean) => {
      if (checked) {
        setSelectedFitStreams([...selectedFitStreams, stream]);
      } else {
        setSelectedFitStreams(selectedFitStreams.filter((s) => s !== stream));
      }
    },
    [selectedFitStreams, setSelectedFitStreams]
  );

  const disableFitButton = useMemo(() => {
    if (earlyTimeStreamsResponse.loading) return true;

    if (
      earlyTimeStreams.some((stream) =>
        selectedFitStreams.includes(stream as MineralType)
      ) &&
      (!selectedTypeCurve?.uniqueTcId ||
        !producingWellBore?.perforatedLateralLengthFt)
    )
      return true;

    const allowedModels = ['hyperbolic', 'modified_hyperbolic'];

    const ratioModels: MineralType[] = [];

    if (oilDcaModelValue === 'ratio') {
      ratioModels.push('oil');
    }
    if (gasDcaModelValue === 'ratio') {
      ratioModels.push('gas');
    }
    if (waterDcaModelValue === 'ratio') {
      ratioModels.push('water');
    }

    const isRatioModelSelected = selectedFitStreams.some((stream) =>
      ratioModels.includes(stream)
    );

    // If no ratio model is selected, enable the fit button
    if (!isRatioModelSelected) return false;

    const mainStream: MineralType | null = allowedModels.includes(
      oilDcaModelValue
    )
      ? 'oil'
      : allowedModels.includes(gasDcaModelValue)
        ? 'gas'
        : null;

    // If a ratio model is selected and the main stream is unavailable or not selected, disable the fit button
    return !!(
      isRatioModelSelected &&
      mainStream &&
      !selectedFitStreams.includes(mainStream)
    );
  }, [
    oilDcaModelValue,
    gasDcaModelValue,
    waterDcaModelValue,
    selectedFitStreams,
    earlyTimeStreams,
    selectedTypeCurve?.uniqueTcId,
    earlyTimeStreamsResponse.loading,
    producingWellBore?.perforatedLateralLengthFt,
  ]);
  const handleFitClick = useFitCheckedStreams({ uwi, producingWellBore });

  return (
    <div className={style.fitStreamsContainer}>
      <label className={style.label}>Fit Streams</label>
      <div className={style.contentWrapper}>
        <div className={style.checkboxGroup}>
          <Checkbox
            id="fit-all"
            labelText="All"
            checked={isAllSelected}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleAllChange(checked)
            }
            disabled={disabled}
          />
          <Checkbox
            id="fit-oil"
            labelText="Oil"
            checked={selectedFitStreams.includes('oil')}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleStreamChange('oil', checked)
            }
            disabled={disabled}
          />
          <Checkbox
            id="fit-gas"
            labelText="Gas"
            checked={selectedFitStreams.includes('gas')}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleStreamChange('gas', checked)
            }
            disabled={disabled}
          />
          <Checkbox
            id="fit-water"
            labelText="Water"
            checked={selectedFitStreams.includes('water')}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleStreamChange('water', checked)
            }
            disabled={disabled}
          />
        </div>
        <Button
          kind="tertiary"
          size="sm"
          onClick={handleFitClick}
          disabled={
            disabled || selectedFitStreams.length === 0 || disableFitButton
          }
          className={style.fitButton}
          renderIcon={CenterCircle}
        >
          Fit
        </Button>
      </div>
    </div>
  );
};
